import {
	AsyncSelect,
	Badge,
	Box,
	Button,
	Component,
	Dropdown,
	Icons,
	Inline,
	Select,
	Stack,
	Table,
	TableDataCell,
	Text,
	Time,
	useDragDrop,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect, useState } from "react"
import { generatePath } from "../router-utils"
import { useXHR } from "@sembark-travel/xhr"
import { LeadChannelXHR, TLeadChannel, useLeadCategoryOptions } from "./store"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { EditLeadChannel } from "./NewItem"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	isTruthy,
	SelectField,
	SwitchInputField,
} from "@sembark-travel/ui-form"
import { $PropertyType } from "utility-types"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import useSWR from "swr"

type TFilters = TSearchParams & {
	disabled_only?: boolean
	lead_channel_category?: Array<{ id: string; name: string }>
	type?: (typeof leadChannelTypeOptions)[number]
}

type TLocationQuery = TSearchParams & {
	disabled_only?: 1
	lead_channel_category?: Array<string>
	type?: (typeof leadChannelTypeOptions)[number]["id"]
}

const leadChannelTypeOptions = [
	{
		id: "manual",
		name: "Manual",
	},
	{
		id: "auto",
		name: "Auto",
	},
]

function filterToQuery(filters: TFilters) {
	const { q, page, disabled_only, lead_channel_category, type } = filters
	const query: TLocationQuery = {}

	if (q) {
		query.q = q
	}

	if (page) {
		query.page = page
	}

	if (disabled_only) {
		query.disabled_only = 1
	}

	if (lead_channel_category) {
		query.lead_channel_category = lead_channel_category.map(
			(lead) => `${lead.id}_${lead.name}`
		)
	}

	if (type) {
		query.type = type.id
	}

	return query
}

function queryToFilter(query: TLocationQuery) {
	const { q, page, disabled_only, lead_channel_category, type } = query
	const filters: TFilters = {}

	if (q) {
		filters.q = q
	}

	if (page) {
		filters.page = page
	}

	if (disabled_only) {
		filters.disabled_only = true
	}

	if (lead_channel_category) {
		filters.lead_channel_category = lead_channel_category.map(
			(lead: string) => {
				const [id, ...name] = lead.split("_")
				return {
					id: id,
					name: name.join("_"),
				}
			}
		) as unknown as $PropertyType<TFilters, "lead_channel_category">
	}

	if (type) {
		filters.type = leadChannelTypeOptions.find((t) => t.id === type)
	}
	return filters
}

function filterToApiParams(filters: TFilters) {
	const { disabled_only, lead_channel_category, type, ...otherParams } = filters

	return {
		...otherParams,
		disabled_only: disabled_only ? 1 : undefined,
		lead_channel_categories: lead_channel_category?.map((c) => c.id),
		is_auto: type?.id === "auto" ? 1 : type?.id === "manual" ? 0 : undefined,
	}
}

export function List() {
	const { hasPermission } = useCheckPermissions()
	const [isReorderDialogOpen, openReorderDialog, closeReorderDialog] =
		useDialog()
	const [query, setQuery] = useLocationQuery({
		toQuery: filterToQuery,
		fromQuery: queryToFilter,
	})
	const [params, setParams] = useSearch(query)

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search
			initialParams={params}
			Filters={Filters}
			title="Lead Channels"
			onSearch={(params) => setParams({ ...params, page: 1 })}
			actions={
				hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Inline gap="4">
						<ButtonLink to={generatePath("/lead-channels/new")} level="primary">
							<Icons.Plus /> New Channel
						</ButtonLink>
						<Dropdown alignRight="sm">
							<Dropdown.ToggleButton>
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem onClick={openReorderDialog}>
									<Icons.SwitchHorizontal rotate="90" /> Reorder Channels
								</Dropdown.MenuItem>
								<Dropdown.MenuItem
									as={Link}
									to={generatePath("/lead-channels/merge")}
								>
									Merge Duplicate Channels
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				) : null
			}
			areAdvancedFiltersApplied={(filters) => {
				const { disabled_only, ...otherFilters } = filters
				return (
					isTruthy(disabled_only) ||
					areAdvancedFiltersAppliedDefault(otherFilters)
				)
			}}
		>
			<ListView
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
				pageKey="lead-channels"
				fetch={(xhr, params) =>
					LeadChannelXHR(xhr).get(filterToApiParams(params))
				}
			>
				{({ items: channels, refresh }) => (
					<>
						<Table
							responsive
							bordered
							alignCols={{ 3: "right" }}
							headers={["Name", "Category", "Created By", ""]}
							rows={channels.map((channel) => [
								<Inline gap="2">
									<Text>{channel.name}</Text>
									{channel.is_auto ? <Badge warning>Auto</Badge> : null}
								</Inline>,
								<Text>
									{channel.lead_channel_category
										? channel.lead_channel_category
										: "-"}
								</Text>,
								<Stack gap="px">
									<Text>{channel?.created_by?.name || "You"}</Text>
									<Text color="muted" fontSize="sm">
										<Time timestamp={channel.created_at} />
									</Text>
								</Stack>,
								!channel.is_auto && hasPermission(PERMISSIONS.MANAGE_USERS) ? (
									<Box>
										<Dropdown alignRight>
											<Dropdown.ToggleButton size="sm" level="tertiary">
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												{!channel.deleted_at ? (
													<Component initialState={false}>
														{({ state, setState }) => (
															<>
																<Dropdown.MenuItem
																	onClick={() => setState(true)}
																>
																	<Icons.Pencil /> Edit Details
																</Dropdown.MenuItem>
																<Dropdown.MenuItemDivider marginY="0" />
																<Dialog
																	open={state}
																	onClose={() => setState(false)}
																	title="Edit Lead Channel Details"
																>
																	<Dialog.Body>
																		<EditLeadChannel
																			channel={channel}
																			onSuccess={() => {
																				setState(false)
																				refresh()
																			}}
																			onCancel={() => {
																				setState(false)
																			}}
																		/>
																	</Dialog.Body>
																</Dialog>
															</>
														)}
													</Component>
												) : null}
												<ChangeChannelStatus
													channel={channel}
													onSuccess={refresh}
												/>
											</Dropdown.Menu>
										</Dropdown>
									</Box>
								) : null,
							])}
						/>
						{isReorderDialogOpen ? (
							<ChannelReorder
								open={isReorderDialogOpen}
								onCancel={closeReorderDialog}
								onSuccess={() => {
									refresh()
									closeReorderDialog()
								}}
							/>
						) : null}
					</>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	const { options: categoryOptions } = useLeadCategoryOptions()
	return (
		<Stack gap="4">
			<SelectField
				name="lead_channel_category"
				select={Select}
				options={categoryOptions}
				label="Channel Category"
				fetchOnMount
				multiple
			/>

			<SelectField
				name="type"
				label="Type"
				select={Select}
				options={leadChannelTypeOptions}
				fetchOnMount
			/>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}

function ChangeChannelStatus({
	channel,
	onSuccess,
}: {
	channel: TLeadChannel
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const [loading, setLoading] = useState(false)

	const isDisabled = Boolean(channel.deleted_at)
	const action = isDisabled ? "enable" : "disable"

	const handleClick = async () => {
		if (loading) return

		if (
			!window.confirm(
				`Are you sure you want to ${action} the Channel ${channel.name} ?`
			)
		) {
			return
		}

		setLoading(true)

		try {
			const resp = isDisabled
				? await LeadChannelXHR(xhr).restore(channel.id)
				: await LeadChannelXHR(xhr).destroy(channel.id)

			showSnackbar(
				resp.message ||
					`Channel ${isDisabled ? "Restored" : "Disabled"} successfully`
			)

			onSuccess()
		} catch (e) {
			const error = e as Error
			window.alert(
				error.message || "Something went wrong. Please try after sometime."
			)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Dropdown.MenuItem
			color={isDisabled ? "success" : "warning"}
			onClick={handleClick}
		>
			{loading ? (
				"Please wait..."
			) : (
				<Inline gap="2" alignItems="center">
					{isDisabled ? <Icons.Refresh /> : <Icons.Ban />}
					{isDisabled ? "Restore" : "Disable"}
				</Inline>
			)}
		</Dropdown.MenuItem>
	)
}

export function SelectLeadChannels({
	hideAutoChannels,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	hideAutoChannels?: boolean
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) =>
				LeadChannelXHR(xhr)
					.get({ q, is_auto: hideAutoChannels ? 0 : null })
					.then((resp) => resp.data)
			}
			{...props}
		/>
	)
}

function ChannelReorder({
	open,
	onCancel,
	onSuccess,
}: {
	open: boolean
	onCancel?: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const { data, isLoading } = useSWR(`/lead-channels/get-all`, () =>
		LeadChannelXHR(xhr).get({ limit: 50, is_auto: 0 })
	)

	const [channels, setChannels] = useState<TLeadChannel[]>([])
	const [saving, setSaving] = useState(false)
	const {
		handleDragStart,
		handleDragOver,
		handleDrop,
		getRowStyle,
		handleDragLeave,
	} = useDragDrop<TLeadChannel>(channels, (updatedChannels) => {
		setChannels(updatedChannels)
	})

	useEffect(() => {
		if (data?.data) {
			setChannels(data.data)
		}
	}, [data])
	return (
		<Dialog open={open} onClose={onCancel} title="Reorder Lead Channels">
			<Dialog.Body>
				{isLoading ? (
					<Text>Loading channels...</Text>
				) : (
					<Table
						bordered
						responsive
						headers={["Priority", "Channel Name", "Category"]}
						children={
							<tbody>
								{channels.map((channel, index) => (
									<Box
										style={{
											...getRowStyle(index),
											cursor: "grab",
										}}
										as="tr"
										key={channel.id}
										draggable
										onDragStart={(e: React.DragEvent<HTMLTableRowElement>) =>
											handleDragStart(e, index)
										}
										onDrop={(e: React.DragEvent<HTMLTableRowElement>) =>
											handleDrop(e, index)
										}
										onDragOver={(e: React.DragEvent<HTMLTableRowElement>) =>
											handleDragOver(e, index)
										}
										onDragLeave={handleDragLeave}
									>
										<TableDataCell>
											<Inline gap="3" alignItems="center">
												<Stack justifyContent="center">
													<Box
														padding="1"
														bgColor="accent"
														color="primary"
														borderWidth="1"
														borderColor="accent"
														rounded="md"
													>
														<Icons.SwitchHorizontal rotate="90" />
													</Box>
												</Stack>
												<Text>{index + 1}</Text>
											</Inline>
										</TableDataCell>

										<TableDataCell>
											<Text fontWeight="semibold">{channel.name}</Text>
										</TableDataCell>

										<TableDataCell>
											<Text>
												{channel.lead_channel_category
													? channel.lead_channel_category
													: "-"}
											</Text>
										</TableDataCell>
									</Box>
								))}
							</tbody>
						}
					/>
				)}
			</Dialog.Body>

			<Dialog.Footer>
				<Inline gap="2" justifyContent="end">
					<Button onClick={onCancel}>Cancel</Button>

					<Button
						level="primary"
						disabled={saving}
						onClick={async () => {
							if (saving) return
							setSaving(true)
							const payload = channels.map(({ id }, index) => ({
								id,
								order: index + 1,
							}))
							try {
								await xhr.patch("lead-channels/reorder", {
									lead_channels: payload,
								})
								showSnackbar("Lead channels reordered successfully")
								onSuccess()
							} catch (e) {
								const error = e as Error
								window.alert(
									error.message ||
										"Something went wrong. Please try again after sometime."
								)
							} finally {
								setSaving(false)
							}
						}}
					>
						{saving ? "Saving..." : "Save"}
					</Button>
				</Inline>
			</Dialog.Footer>
		</Dialog>
	)
}
